import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppState, Platform, Pressable, StyleSheet, View } from 'react-native';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { COLUMNS, ROWS, createSnake, stepSnake, turnSnake } from '@/lib/waitingSnake';

const keys = { ArrowUp: 'up', Up: 'up', w: 'up', ArrowDown: 'down', Down: 'down', s: 'down', ArrowLeft: 'left', Left: 'left', a: 'left', ArrowRight: 'right', Right: 'right', d: 'right' };
const createStyles = colors => StyleSheet.create({
  game: { alignItems: 'center', gap: 8, maxWidth: '100%' },
  header: { width: '100%', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  actions: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  smallButton: { minHeight: 32, minWidth: 44, paddingHorizontal: 8, alignItems: 'center', justifyContent: 'center', borderRadius: 8 },
  field: { borderRadius: 12, overflow: 'hidden', backgroundColor: colors.solid_color_primary },
  board: { ...StyleSheet.absoluteFillObject, ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}) },
  segment: { position: 'absolute', backgroundColor: colors.text_color_primary, borderRadius: 3 },
  food: { position: 'absolute', backgroundColor: colors.text_color_secondary, borderRadius: 8 },
  overlay: { ...StyleSheet.absoluteFillObject, backgroundColor: colors.solid_color_primary, alignItems: 'center', justifyContent: 'center', gap: 12, padding: 16 },
  description: { textAlign: 'center' },
  playButton: { minHeight: 44, paddingHorizontal: 20, borderRadius: 10, backgroundColor: colors.text_color_primary, alignItems: 'center', justifyContent: 'center' },
});

function WaitingSnakeGame({ colors, compact = false, fieldWidth, onHide }) {
  const styles = useMemo(() => createStyles(colors), [colors]);
  const width = fieldWidth ?? (compact ? 216 : 280);
  const cell = width / COLUMNS;
  const [phase, setPhase] = useState('ready');
  const [state, setState] = useState(createSnake);
  const stateRef = useRef(state);
  const boardRef = useRef(null);
  const changedDirection = useRef(false);
  const [best, setBest] = useState(0);
  const [keyboardFocus, setKeyboardFocus] = useState(false);
  const pause = useCallback(() => setPhase(current => current === 'playing' ? 'paused' : current), []);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', status => { if (status !== 'active') pause(); });
    const onVisibility = () => { if (document.hidden) pause(); };
    if (Platform.OS === 'web') document.addEventListener('visibilitychange', onVisibility);
    return () => {
      subscription.remove();
      if (Platform.OS === 'web') document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [pause]);
  useEffect(() => {
    if (phase !== 'playing') return undefined;
    boardRef.current?.focus?.();
    const timer = setInterval(() => {
      const next = stepSnake(stateRef.current);
      stateRef.current = next;
      changedDirection.current = false;
      setState(next);
      setBest(current => Math.max(current, next.score));
      if (next.ended) setPhase('ended');
    }, 220);
    return () => clearInterval(timer);
  }, [phase]);

  const play = () => {
    if (phase !== 'paused') {
      const next = createSnake();
      stateRef.current = next;
      changedDirection.current = false;
      setState(next);
    }
    setPhase('playing');
  };
  const onKeyDown = event => {
    const native = event.nativeEvent || {};
    const rawKey = native.key || event.key || native.charactersIgnoringModifiers || { 123: 'ArrowLeft', 124: 'ArrowRight', 125: 'ArrowDown', 126: 'ArrowUp', 49: ' ' }[native.keyCode] || '';
    const key = rawKey.length === 1 ? rawKey.toLowerCase() : rawKey;
    if (phase !== 'playing' && phase !== 'paused') return;
    if (keys[key] || key === ' ') {
      event.preventDefault?.();
      setKeyboardFocus(true);
      if (key === ' ') { if (phase === 'playing') pause(); else play(); }
      else if (phase === 'playing' && !changedDirection.current) {
        const next = turnSnake(stateRef.current, keys[key]);
        if (next !== stateRef.current) changedDirection.current = true;
        stateRef.current = next;
      }
    }
  };

  return (
    <View style={styles.game} testID="waiting-snake-game">
      <View style={styles.header}>
        <Text size="xs" weight="medium">Score · {state.score} <Text size="xxs" color="secondary">Best {best}</Text></Text>
        <View style={styles.actions}>
          {phase === 'playing' && <Pressable accessibilityRole="button" accessibilityLabel="Pause Snake" onPress={pause} style={styles.smallButton} hitSlop={6}><Icon name="pause" size={12} color={colors.text_color_secondary} /></Pressable>}
          <Pressable accessibilityRole="button" accessibilityLabel="Hide Snake" onPress={onHide} style={styles.smallButton} hitSlop={6}><Text size="xs" color="secondary">Games</Text></Pressable>
        </View>
      </View>
      <View testID="waiting-snake-field" style={[styles.field, { width, height: cell * ROWS }]}>
        {state.snake.map((p, index) => <View key={index} style={[styles.segment, { left: p.x * cell + 1, top: p.y * cell + 1, width: cell - 2, height: cell - 2, opacity: index ? 0.65 : 1 }]} />)}
        {state.food && <View style={[styles.food, { left: state.food.x * cell + 3, top: state.food.y * cell + 3, width: cell - 6, height: cell - 6 }]} />}
        {phase === 'playing' && <Pressable ref={boardRef} accessibilityRole="button" accessibilityLabel="Snake board. Use arrow keys or WASD to steer, Space to pause." onPress={() => { setKeyboardFocus(false); boardRef.current?.focus?.(); }} onKeyDown={onKeyDown}
          {...(Platform.OS === 'macos' ? { keyDownEvents: [...Object.keys(keys), ' '].map(key => ({ key })), enableFocusRing: false } : {})}
          onFocus={event => setKeyboardFocus(!!event.nativeEvent?.target?.matches?.(':focus-visible'))} onBlur={() => setKeyboardFocus(false)} style={[styles.board, keyboardFocus && { borderWidth: 1, borderColor: colors.text_color_secondary, borderRadius: 12 }]} />}
        {phase !== 'playing' && <View style={styles.overlay}>
          <Text size="m" weight="medium">{phase === 'ready' ? 'Snake' : phase === 'paused' ? 'Paused' : `Nice run! Score: ${state.score}`}</Text>
          {phase === 'ready' && <Text size="xs" color="secondary" style={styles.description}>Collect dots. We’ll turn at the walls.</Text>}
          <Pressable accessibilityRole="button" accessibilityLabel={phase === 'paused' ? 'Resume Snake' : phase === 'ended' ? 'Play Snake again' : 'Start Snake'} onPress={play} style={styles.playButton}>
            <Text size="s" weight="medium" style={{ color: colors.accent_text_color }}>{phase === 'paused' ? 'Resume' : phase === 'ended' ? 'Play again' : 'Let’s play'}</Text>
          </Pressable>
        </View>}
      </View>
      <Text size="xxs" color="secondary">Arrow keys / WASD · Space to pause</Text>
    </View>
  );
}

export default React.memo(WaitingSnakeGame);
